import { useEffect, useState } from "react";
import DashboardShell from "../components/shell/DashboardShell";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import {
  ScrollText,
  Filter,
  ChevronDown,
  ChevronUp,
  User,
  ArrowRight,
  RefreshCw,
  Loader2,
  ShieldAlert,
  Edit2,
  Gift,
  Trash2,
  LogIn,
  UserPlus,
  UserMinus,
  Download,
} from "lucide-react";
import { authenticatedDownload } from "../lib/pdf";

const ACTION_META = {
  impersonate_user:        { label: "Login as user",      icon: LogIn,      tone: "bg-amber-100 text-amber-800 border-amber-200" },
  change_plan:             { label: "Change plan",        icon: Gift,       tone: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  update_profile:          { label: "Edit profile",       icon: Edit2,      tone: "bg-blue-100 text-blue-800 border-blue-200" },
  delete_landlord:         { label: "Delete user",        icon: Trash2,     tone: "bg-rose-100 text-rose-800 border-rose-200" },
  admin_invited:           { label: "Admin invited",      icon: UserPlus,   tone: "bg-violet-100 text-violet-800 border-violet-200" },
  admin_removed:           { label: "Admin removed",      icon: UserMinus,  tone: "bg-slate-200 text-slate-700 border-slate-300" },
};

const PAGE_SIZE = 100;

export default function AdminAuditLog() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [availableActions, setAvailableActions] = useState([]);
  const [actionFilter, setActionFilter] = useState("");
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);

  const load = async (opts = {}) => {
    const { append = false, freshSkip = 0 } = opts;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("limit", String(PAGE_SIZE));
      params.set("skip", String(freshSkip));
      if (actionFilter) params.set("action", actionFilter);
      const { data } = await api.get(`/admin/audit-log?${params.toString()}`);
      setItems(append ? [...items, ...(data.items || [])] : (data.items || []));
      setTotal(data.total || 0);
      setHasMore(!!data.has_more);
      setSkip(freshSkip + (data.items?.length || 0));
      if (data.available_actions) setAvailableActions(data.available_actions);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't load audit log");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load({ append: false, freshSkip: 0 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionFilter]);

  const loadMore = () => load({ append: true, freshSkip: skip });

  const exportCsv = async () => {
    try {
      const params = new URLSearchParams();
      if (actionFilter) params.set("action", actionFilter);
      const qs = params.toString();
      const today = new Date().toISOString().slice(0, 10);
      await authenticatedDownload(
        `/api/admin/audit-log/export.csv${qs ? `?${qs}` : ""}`,
        `realtyvault-audit-log-${today}${actionFilter ? `-${actionFilter}` : ""}.csv`,
      );
      toast.success("Audit log CSV downloaded");
    } catch (e) {
      toast.error(`Export failed: ${e.message || e}`);
    }
  };

  return (
    <DashboardShell>
      <div className="p-8 max-w-5xl mx-auto">
        <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
          <div>
            <div className="overline text-slate-500">Platform admin</div>
            <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3" style={{ fontFamily: "Outfit" }} data-testid="audit-log-heading">
              <ScrollText className="w-7 h-7 text-slate-700" /> Admin Audit Log
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Every admin action — plan changes, profile edits, deletions, impersonations — recorded in chronological order.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={exportCsv}
              className="btn-outline-dark text-xs flex items-center gap-1"
              data-testid="audit-log-export-csv"
              title={actionFilter ? `Export CSV — only ${actionFilter} events` : "Export full audit trail as CSV"}
            >
              <Download className="w-3.5 h-3.5" /> Export CSV
            </button>
            <button
              onClick={() => load({ append: false, freshSkip: 0 })}
              className="btn-outline-dark text-xs flex items-center gap-1"
              data-testid="audit-log-refresh"
            >
              <RefreshCw className="w-3.5 h-3.5" /> Refresh
            </button>
          </div>
        </div>

        {/* Filter pills */}
        <div className="flex items-center gap-2 mb-5 flex-wrap" data-testid="audit-log-filters">
          <span className="overline text-slate-500 inline-flex items-center gap-1">
            <Filter className="w-3 h-3" /> Filter
          </span>
          <button
            onClick={() => setActionFilter("")}
            className={`text-xs font-mono uppercase tracking-widest px-2 py-1 rounded-sm border transition-colors ${
              actionFilter === "" ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 text-slate-700 hover:bg-slate-50"
            }`}
            data-testid="audit-filter-all"
          >
            All ({total})
          </button>
          {availableActions.map((a) => {
            const meta = ACTION_META[a] || { label: a, tone: "bg-slate-100 text-slate-700 border-slate-200" };
            return (
              <button
                key={a}
                onClick={() => setActionFilter(a)}
                className={`text-xs font-mono uppercase tracking-widest px-2 py-1 rounded-sm border transition-colors ${
                  actionFilter === a ? "bg-slate-900 text-white border-slate-900" : `${meta.tone} hover:opacity-80`
                }`}
                data-testid={`audit-filter-${a}`}
              >
                {meta.label}
              </button>
            );
          })}
        </div>

        {/* Timeline */}
        <div className="bg-white border border-slate-200 rounded-sm">
          {loading && items.length === 0 ? (
            <div className="py-16 text-center text-sm text-slate-500 flex items-center justify-center gap-2">
              <Loader2 className="w-5 h-5 animate-spin" /> Loading audit trail…
            </div>
          ) : items.length === 0 ? (
            <div className="py-16 text-center text-sm text-slate-500" data-testid="audit-log-empty">
              No matching audit events.
            </div>
          ) : (
            <ul className="divide-y divide-slate-100" data-testid="audit-log-timeline">
              {items.map((row) => {
                const meta = ACTION_META[row.action] || { label: row.action, icon: ShieldAlert, tone: "bg-slate-100 text-slate-700 border-slate-200" };
                const Icon = meta.icon;
                const expanded = expandedId === row.id;
                const actorEmail = row.actor_now?.email || row.actor_email || "—";
                const targetEmail = row.target_now?.email || row.target_email || "—";
                const ts = row.at ? new Date(row.at) : null;
                const tsStr = ts ? ts.toLocaleString() : "—";
                return (
                  <li
                    key={row.id || `${row.actor_id}-${row.at}`}
                    className="px-5 py-3.5 hover:bg-slate-50 cursor-pointer"
                    onClick={() => setExpandedId(expanded ? null : row.id)}
                    data-testid={`audit-row-${row.id}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`shrink-0 w-9 h-9 rounded-sm border flex items-center justify-center ${meta.tone}`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap text-sm">
                          <span className="font-semibold text-slate-900">{meta.label}</span>
                          <span className="text-xs font-mono text-slate-500">{tsStr}</span>
                        </div>
                        <div className="flex items-center gap-1.5 text-xs text-slate-700 mt-1 flex-wrap">
                          <User className="w-3 h-3 text-slate-400" />
                          <span className="font-mono">{actorEmail}</span>
                          {row.target_id && (
                            <>
                              <ArrowRight className="w-3 h-3 text-slate-400" />
                              <span className="font-mono">{targetEmail}</span>
                            </>
                          )}
                        </div>
                      </div>
                      {expanded ? <ChevronUp className="w-4 h-4 text-slate-400 mt-2 shrink-0" /> : <ChevronDown className="w-4 h-4 text-slate-400 mt-2 shrink-0" />}
                    </div>
                    {expanded && (
                      <div className="mt-3 pl-12 text-xs">
                        <pre className="bg-slate-900 text-emerald-200 rounded-sm p-3 overflow-x-auto font-mono whitespace-pre-wrap break-words" data-testid={`audit-row-${row.id}-details`}>
                          {JSON.stringify(row, null, 2)}
                        </pre>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {hasMore && (
          <div className="flex justify-center mt-4">
            <button
              onClick={loadMore}
              disabled={loading}
              className="btn-outline-dark text-xs flex items-center gap-1 disabled:opacity-60"
              data-testid="audit-log-load-more"
            >
              {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ChevronDown className="w-3.5 h-3.5" />}
              Load older events
            </button>
          </div>
        )}

        <div className="text-[11px] text-slate-400 mt-4 text-center">
          Showing {items.length} of {total} event{total === 1 ? "" : "s"}
          {actionFilter && <> · filtered by <span className="font-mono">{actionFilter}</span></>}
        </div>
      </div>
    </DashboardShell>
  );
}
